import { fabric } from 'fabric';
import {IEditorCache, IEditorCanvas, IEditorEditorImage, IEditorImage, IEditorThumb, Imports} from "./editor.interfaces";

/* tslint:disable no-invalid-this */
const globalImports: Imports = typeof global !== 'undefined' ? global as any : window as any;

/**
 * - Do not read ThumbSize from SF.Model at module load time (order-dependent global).
 * - Keep ThumbSize as an explicit constant to avoid init-order crashes.
 */
export const ThumbSize = {
  SMALL: 'small',
  MEDIUM: 'medium',
  LARGE: 'large',
  XLARGE: 'xlarge',
  FULL: 'full',
} as const;

export class EditorCache implements IEditorCache {
    public maxFullSizeImage:number = 7000;

    private loaded: { [key: string]: boolean } = {};
    private orderedThumbs: string[] = [
        ThumbSize.SMALL,
        ThumbSize.MEDIUM,
        ThumbSize.LARGE,
        ThumbSize.XLARGE,
        ThumbSize.FULL
    ];

    public isLoaded(url: string): boolean {
        return this.loaded.hasOwnProperty(url);
    }

    public markLoaded(url: string): void {
        this.loaded[url] = true;
    }

    public markWhenLoaded(url: string): void {
        if (!this.isLoaded(url)) {
            fabric.util.loadImage(url, (): void => {
                    this.markLoaded(url);
                }, this, 'anonymous'
            );
        }
    }

    public getTotalZoom(canvas: IEditorCanvas, image: IEditorEditorImage): number {
        let imageZoom = 1.0;
        if (image.imagePosition && 0 < image.imagePosition.zoom) {
            imageZoom = image.imagePosition.zoom;
        } else if (image.width && image.image && image.image.width) {
            imageZoom = image.width / image.image.width;
        }

        return (canvas.getZoom ? canvas.getZoom() : 1) * imageZoom;
    }

    public getOptimalUrl(image: IEditorImage, optimalSize: string, autoCorrect: boolean, autoRedEye: boolean): string {
        const optimalThumb = image.getThumb(optimalSize);
        return this.getUrl(image, optimalThumb, autoCorrect, autoRedEye);
    }

    public smartLoad(element: HTMLImageElement, image: IEditorImage, scale: number, loadThumbIfCached: boolean,
                     callback: (img: HTMLImageElement | null, optimal: boolean, dontRetry?: boolean) => void,
                     autoCorrect: boolean, autoRedEye: boolean, hasFilterEffect: boolean, imageWithFrame: boolean,
                     editorPhoto?: IEditorEditorImage): void {
        // first, we need to find optimal thumb size
        const optimalSize = this.getOptimalSize(image, scale, hasFilterEffect, imageWithFrame, editorPhoto);
        const optimalUrl = this.getOptimalUrl(image, optimalSize, autoCorrect, autoRedEye);

        // if the optimal thumb is not loaded, we search the cache
        if (!this.isLoaded(optimalUrl)) {
            // variables for thumb search
            let thumb: IEditorThumb;
            let thumbUrl: string;
            const optimalIndex: number = this.orderedThumbs.indexOf(optimalSize);

            // optimization: if a larger thumb is already loaded, use as optimal
            // end the loop at -1 as we don't want to get the full size -- note window.productionImage is only used in the Android Kiosk...
            for (let i = optimalIndex + 1; i < this.orderedThumbs.length - (fabric.isLikelyNode || (window as any).productionImage ? 0 : 1); i++) {
                thumb = image.getThumb(this.orderedThumbs[i]);

                if (thumb) {
                    thumbUrl = this.getUrl(image, thumb, autoCorrect, autoRedEye);

                    if (this.isLoaded(thumbUrl)) {
                        if (!element.src || element.src.replace(/file:\/+/, '') !== thumbUrl.replace(/file:\/+/, '')) {
                            this.loadImage(thumbUrl, callback, true);
                        } else {
                            callback(element, true);
                        }
                        return;
                    }
                }
            }

            // if the optimal image is not yet loaded, try to look for a smaller thumb that is immediately available
            // this should not happen for StaticCanvas
            if (loadThumbIfCached) {
                let startIndex = optimalIndex;
                if (this.orderedThumbs[optimalIndex] == 'full') {
                    startIndex -= 1;
                }
                for (let i = startIndex; i-- > 0;) {
                    thumb = image.getThumb(this.orderedThumbs[i]);

                    if (thumb) {
                        thumbUrl = this.getUrl(image, thumb, autoCorrect, autoRedEye);

                        if (this.isLoaded(thumbUrl)) {
                            if (!element.src || element.src.replace(/file:\/+/, '') !== thumbUrl.replace(/file:\/+/, '')) {
                                this.loadImage(thumbUrl, callback, false);
                            } else {
                                callback(element, false);
                            }
                            break;
                        }
                    }
                }
            }
        }

        // at last, start loading original right away
        if (!element.src || element.src.replace(/file:\/+/, '') !== optimalUrl.replace(/file:\/+/, '')) {
            this.loadImage(optimalUrl, callback, true);
        } else {
            callback(element, true);
        }
    }

    public canUseFullSizedImage(hasFilterEffect: boolean, largestSide:number): boolean {
        // Filter effects on large images causes crashes on Android and mobile devices.
        // Should test this on mobile devices aas well..
        return fabric.isLikelyNode || !((hasFilterEffect && largestSide > 2000) || largestSide > this.maxFullSizeImage);
    }

    public getOptimalSize(image: IEditorImage, scale: number, hasFilterEffect?: boolean, imageWithFrame?: boolean, editorPhoto?: IEditorEditorImage): string {
        let largestAvailableThumb: string = 'full';

        // Mobile devices are getting small images which leads to bad 3D previews...  Load an image that takes into account
        // the resolution of the mobile device.
        if (typeof window !== 'undefined') {
            // "window" is not defined in JRS.
            scale *= window.devicePixelRatio || 1;
        }

        // Use the production image in the Android kiosk if we are creating production images, also use it in the JRS (isLikelyNode).
        const largestThumb = image.getThumb(this.orderedThumbs[this.orderedThumbs.length - 1]);
        const useFullSizedImage = this.canUseFullSizedImage(!!hasFilterEffect, Math.max(largestThumb.width, largestThumb.height));

        let isCanvasEmpty = false;

        if (!fabric.isLikelyNode && editorPhoto && editorPhoto.canvas && editorPhoto.canvas.getZoom() !== 1) {
            if (editorPhoto.canvas.getObjects().length === 1 && editorPhoto.imagePosition && editorPhoto.imagePosition.zoom === 1) {
                isCanvasEmpty = true;
            }
        }

        for (let i = 0; i < this.orderedThumbs.length - (useFullSizedImage ? 0 : 1); i++) {
            const thumbSize: string = this.orderedThumbs[i];
            const thumb: IEditorThumb = image.getThumb(thumbSize);
            if (thumb && image.thumbs[thumbSize] && editorPhoto) {
                largestAvailableThumb = thumbSize;

                // in customizer when canvas is empty and the photo is uploaded on the canvas for the first time, imagePosition is not set
                // yet and it's default to one, thus the scale cannot be compared with thumb.width/image.width (PS-62617)
                const thumbScale = isCanvasEmpty ? Math.min(thumb.width / editorPhoto.imagePosition.width, thumb.height / editorPhoto.imagePosition.height) :
                    Math.min(thumb.width / image.width, thumb.height / image.height);
                if (thumbScale >= scale) {
                    return thumbSize;
                }
            }
        }

        /* The following 'if statement' leads to phase out issue in Cards (it loads the full size image which breaks the canvas in chrome).
         We remove it for now but needs more testing to make sure it doesn't create other issues...*/

        // If we are less than 75% of the scale, then we will use the full size image...
        if (useFullSizedImage && image.getThumb(largestAvailableThumb).width / image.width < (0.75 * scale) && image.getThumb(ThumbSize.FULL)) {
            return ThumbSize.FULL;
        }

        return largestAvailableThumb;
    }

    private  getDirectoryForFile  (dir: any, filename: string):Promise<{directory: any, filename: string}>  {
        return new Promise<{directory: any, filename: string}>((resolve:(result:{directory: any, filename: string}) => void, reject:(error:string) => void):void => {
            if (filename.indexOf('/') > -1) {
                const pathParts = filename.split('/');
                const getDir = pathParts[0];
                dir.getDirectory(getDir, {create: true}, (subDirEntry: any): void => {
                    filename = pathParts.slice(1).join('/');
                    this.getDirectoryForFile(subDirEntry, filename).then(directory => {
                        resolve({directory, filename});
                    });
                }, (error: any) => {
                    reject('Unable to get directory for filename: ' + filename + ' error: ' + JSON.stringify(error));
                });
            } else {
                resolve({directory: dir, filename});
            }
        });
    }

    private getFilename(fileUrl: string): string {
        // NOTE: We know this is a valid directory because we created it in the template-picker-controller...
        return 'template-image/' + fileUrl.substring(fileUrl.lastIndexOf('/') + 1);
    }

    public loadFromWeb(webUrl: string): Promise<HTMLImageElement>  {
        return new Promise<HTMLImageElement>((resolve:(img:HTMLImageElement) => void, reject:(error:string) => void):void => {
            // XXX: This is a hack to get around the fact that node-canvas currently doesn't support WEBP.
            //      As soon as it does, remove this since it is very slow to convert webp to png.  This version
            //      is if the ITE isn't loading images (this assumes that ite's have -ite. in their hostname.
            if (fabric.isLikelyNode && webUrl.endsWith('.webp') &&
                webUrl.indexOf('ite.') === -1 && globalImports.SF.Util.WebpImageConverter) {

                globalImports.SF.Util.WebpImageConverter(webUrl).then((img: HTMLImageElement) => {
                    resolve(img);
                }).catch((e: unknown) => {
                    reject('Unable to load webp image ' + JSON.stringify(e));
                });

            } else {
                // This uses the ITE to convert the webp into a png so that node-canvas can handle the image.  This
                // is still slow, so we still want to remove it when node-canvas supports webp
                if (fabric.isLikelyNode && webUrl.endsWith('.webp')) {
                    webUrl += '?type=png';
                }
                fabric.util.loadImage(webUrl, (img: HTMLImageElement | null) => {
                    if (img == null) {
                        reject('Unable to load image');
                    } else {
                        resolve(img);
                    }
                }, this, 'anonymous');
            }
        });
    }

    /**
     * Loads image element from given url and passes it to a callback
     * @param {String} url URL representing an image
     * @param {Function} originalCallback Callback; invoked with loaded image
     * @param {boolean} isOptimal
     */
    public loadImage(url: string,
             originalCallback: (img: HTMLImageElement | null, optimal: boolean, dontRetry: boolean) => void,
             isOptimal: boolean): void {
        const errorCallback= (message:string) => {
            console.error('Error: ' + message + ', Unable to load image: ' + url);
            if (typeof global !== 'undefined') {
                (global as any).renderResult = (global as any).renderResult || {
                    success: false,
                    url: '',
                    message: 'Error: ' + message
                };
                (global as any).renderResult.success = false;
                (global as any).renderResult.url = url;
            }
            originalCallback(null, isOptimal, true);
        };

        const callback = (img: HTMLImageElement | null): void => {
            this.markLoaded(url);
            // call original callback
            try {
                originalCallback(img, isOptimal, false);
            } catch (ex) {
                errorCallback(ex.message);
            }

            if (img && img.src.substring(0, 4) == 'http') {
                // Window has to be protected because we could be running in Node and node has no window!
                // TODO: move to cache-android.ts
                if (typeof window !== 'undefined' && (window as any).cordova && (window as any).resolveLocalFileSystemURL) {
                    this.loadAndroidImage(url, img);
                }
            }
        };

        if (url.substring(0, 4) === 'http') {
            const filename = this.getFilename(url);
            // TODO: Move to cache-android.ts...
            if (typeof window !== 'undefined' && (window as any).cordova && (window as any).resolveLocalFileSystemURL) {
                (window as any).resolveLocalFileSystemURL((window as any).cordova.file.externalApplicationStorageDirectory + filename, (fileEntry: any): void => {
                    const image = new Image();
                    image.onload = (): void => {
                        callback(image);
                    };
                    image.onerror = (): void => {
                        errorCallback('Error loading ' + fileEntry.toURL());
                    };
                    image.src = fileEntry.toURL();
                }, (): void => {
                    errorCallback('Error resolving file system url');
                });
            } else {
                this.loadFromWeb(url).then(img => {
                    callback(img);
                }, err => {
                    errorCallback('Error loading from web ' + url + ' Error: ' + JSON.stringify(err));
                });
            }
        } else {
            this.loadFromWeb(url).then(img => {
                callback(img);
            }, err => {
                errorCallback('Error loading ' + url + ' Error: ' + JSON.stringify(err));
            });
        }
    }

    private base64ToBlob (base64: string, contentType: string = '', sliceSize: number = 512):Blob  {
        if (base64.substring(0, 5) === 'data:') {
            base64 = base64.substring(base64.indexOf(',') + 1);
        }
        const byteCharacters: string = atob(base64);
        const byteArrays: Uint8Array[] = [];
        for (let offset = 0; offset < byteCharacters.length; offset += sliceSize) {
            const slice: string = byteCharacters.slice(offset, offset + sliceSize);
            const byteNumbers: number[] = new Array(slice.length);
            for (let i = 0; i < slice.length; i++) {
                byteNumbers[i] = slice.charCodeAt(i);
            }
            byteArrays.push(new Uint8Array(byteNumbers));
        }
        return new Blob(byteArrays, { type: contentType });
    }

    private loadAndroidImage(url: string, img: HTMLImageElement):void {
        const filenameFromUrl = this.getFilename(url);
        (window as any).resolveLocalFileSystemURL((window as any).cordova.file.externalApplicationStorageDirectory, (cacheDir: any): void => {
            this.getDirectoryForFile(cacheDir, filenameFromUrl).then((result: {
                directory: any,
                filename: string
            }): void => {
                result.directory.getFile(result.filename, {create: true}, (file: any): void => {
                    file.createWriter((fileWriter: any): void => {
                        if (img) {
                            const canvas = document.createElement('canvas');
                            canvas.width = img.width;
                            canvas.height = img.height;

                            // Copy the image contents to the canvas
                            const ctx = canvas.getContext('2d');
                            if (ctx) {
                                ctx.drawImage(img, 0, 0);
                            }
                            const ext = url.substring(url.lastIndexOf('.') + 1);
                            const type = ext === 'jpg' ? 'image/jpeg' : 'image/png';
                            fileWriter.write(this.base64ToBlob(canvas.toDataURL(type), type));
                        } else {
                            console.error('img is somehow null');
                        }
                    });
                }, (error: any): void => {
                    console.error('Unable to get file for path: ' + filenameFromUrl + ' filename: ' + result.filename, error);
                });
            }).catch(err => {
                console.error('Unable to get directory for file: ' + err);
            });
        });
    }

    /**
     *
     * @param img
     * @param {String} url URL representing an image
     * @param {Function} callback Callback; invoked with loaded image
     * @param {boolean} isOptimal
     */
    public loadImageWithElement(img: HTMLImageElement, url: string, callback: (img: HTMLImageElement | null, optimal: boolean) => void, isOptimal: boolean): void {
        img.onload = (): void => {
            img.onload = img.onerror = (null as any);

            // mark image as loaded
            this.markLoaded(url);

            // execute the callback
            if (callback) {
                callback.call(null, img, isOptimal);
            }

            // nullify the image
            (img as any) = null;
        };

        img.onerror = function (): void {
            fabric.log('Error loading ' + img.src);
            if (fabric.isLikelyNode) {
                (global as any).renderResult.info += 'Failed to load image url: ' + url;
                (global as any).renderResult.success = false;
            }
            img = img.onload = img.onerror = (null as any);
            if (callback) {
                callback.call(null, null, isOptimal);
            }
        };

        // set the source of the image
        img.src = url;
    }

    private getUrl(image: IEditorImage, thumb: IEditorThumb, autoCorrect: boolean, autoRedEye: boolean): string {
        return image.getUrl(thumb.name, autoCorrect, autoRedEye);
    }
}
